import React from 'react';
import { Box, Typography } from '@mui/material';
import CustomModal from '../../shared/CustomModal/CustomModal';
import CustomCard from '../../shared/CustomCard/CustomCard';
import zeplinColor from '../../../theme/Color';
import { TeamService } from '../../../service/team.service';
import { initialAlertState, useAlertStatus } from '../../../customHook/useAlertStatus';
import CustomAlert from '../../shared/CustomAlert/CustomAlert';

type DeleteAssignmentModalProps = {
  open: boolean;
  assignmentId?: string | null;
  assignmentName?: string;
  teamMemberName?: string;
  capacityWeight?: number;
  onClose: () => void;
  onDeleted?: () => void;
};

const DeleteAssignmentModal = ({
  open,
  assignmentId,
  assignmentName,
  teamMemberName,
  capacityWeight,
  onClose,
  onDeleted,
}: DeleteAssignmentModalProps) => {
  const [deleting, setDeleting] = React.useState(false);
  const { alertState, startOperation, completeOperation, setAlertState, getAlertContent } =
    useAlertStatus();

  const handleDeleteConfirm = async () => {
    if (!assignmentId || deleting) return;

    setDeleting(true);
    try {
      startOperation('delete', 'Deleting assignment...');
      await TeamService.deleteAssignment(assignmentId);
      completeOperation('delete', 'Assignment deleted successfully.');
      onDeleted && onDeleted();
      onClose();
    } catch (error: any) {
      completeOperation(
        'delete',
        error?.response?.data?.error ||
          error?.response?.data?.message ||
          'Failed to delete assignment.',
        true,
      );
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <CustomAlert
        open={alertState.status !== 'idle'}
        handleClose={() => setAlertState(initialAlertState)}
        message={getAlertContent(alertState)}
        error={alertState.status === 'error'}
      />

      <CustomModal handleClose={onClose} open={open}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
          }}
        >
          <CustomCard
            styles={{
              borderRadius: '14px',
              padding: { xs: '20px', xl: '24px' },
              backgroundColor: zeplinColor.Neutral1,
              boxShadow: 'rgba(0, 0, 0, 0.15)',
              width: '448px',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: { xs: '22px', xl: '32px' },
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: { xs: '10px', xl: '16px' },
                }}
              >
                <Typography variant='heading3' color={zeplinColor.BasicBlack}>
                  Unassign Client?
                </Typography>
                <Typography variant='body2' color={zeplinColor.BasicBlack}>
                  You are removing client{' '}
                  <Typography
                    variant='responsiveHeading4'
                    component='span'
                    color={zeplinColor.BasicBlack}
                  >
                    {assignmentName || 'this assignment'}
                  </Typography>{' '}
                  from{' '}
                  <Typography
                    variant='responsiveHeading4'
                    component='span'
                    color={zeplinColor.BasicBlack}
                  >
                    {teamMemberName || 'this team member'}
                  </Typography>
                  . This will release{' '}
                  <Typography
                    variant='responsiveHeading4'
                    component='span'
                    color={zeplinColor.BasicBlack}
                  >
                    {`${capacityWeight ?? 0}%`}
                  </Typography>{' '}
                  of their workload capacity and remove the entity from their assigned list. Are you
                  sure you want to proceed?
                </Typography>
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  gap: { xs: '10px', xl: '16px' },
                }}
              >
                <Box onClick={onClose} sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
                  <CustomCard
                    styles={{
                      borderRadius: '14px',
                      padding: '14px 32px',
                      backgroundColor: zeplinColor.Neutral1,
                      border: `1px solid ${zeplinColor.BasicBlack}`,
                      cursor: 'pointer',
                      display: 'flex',
                      justifyContent: 'center',
                      flex: 1,
                    }}
                    aria-label='button'
                    aria-disabled={deleting}
                  >
                    <Typography variant='buttonMedium' color={zeplinColor.BasicBlack}>
                      Cancel
                    </Typography>
                  </CustomCard>
                </Box>

                <Box
                  onClick={handleDeleteConfirm}
                  sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}
                >
                  <CustomCard
                    styles={{
                      borderRadius: '14px',
                      padding: '14px 32px',
                      backgroundColor: zeplinColor.Error,
                      cursor: 'pointer',
                      display: 'flex',
                      justifyContent: 'center',
                      flex: 1,
                    }}
                    aria-label='button'
                    aria-disabled={deleting}
                  >
                    <Typography
                      variant='buttonMedium'
                      color={zeplinColor.Neutral1}
                      sx={{ whiteSpace: 'nowrap', textAlign: 'center' }}
                    >
                      {deleting ? 'Unassigning...' : 'Unassign Client'}
                    </Typography>
                  </CustomCard>
                </Box>
              </Box>
            </Box>
          </CustomCard>
        </Box>
      </CustomModal>
    </>
  );
};

export default DeleteAssignmentModal;
